import { useMemo } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import filterFactory, {
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { UserInfos } from "../../../../redux/types";
import {
  getMagicLinkState,
  getOnboardingStep,
  isOnboardingOpen,
} from "../../../../utils/bcs-onboarding";

// Une ligne par utilisateur dont l'onboarding n'est pas terminé. On aplatit
// ici ce dont les colonnes ont besoin : BootstrapTable filtre sur des champs,
// pas sur des chemins calculés.
type Row = {
  uid: string;
  utilisateur: string;
  email: string;
  entreprise: string;
  identifiant: string;
  etape: string;
  statut?: string;
  updatedAt?: number;
  user: UserInfos;
};

const dateTime = (ms?: number) =>
  ms
    ? new Date(ms).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

function OnboardingsTable({ users }: { users: UserInfos[] }) {
  const rows: Row[] = useMemo(
    () =>
      users
        .filter((u) => isOnboardingOpen(u.bcs?.onboardingStatus))
        .map((u) => {
          const b = u.bcs!;
          const t = b.target || {};
          return {
            uid: u.uid as string,
            utilisateur: `${u.prenom || ""} ${u.nom || ""}`.trim() || "—",
            email: u.email,
            entreprise: t.name || t.organizationName || "—",
            identifiant: t.identifier
              ? `${t.typeSiret ? t.typeSiret + " " : ""}${t.identifier}`
              : "—",
            etape: getOnboardingStep(b.onboardingStatus).label,
            statut: b.onboardingStatus,
            updatedAt: b.updatedAt,
            user: u,
          };
        })
        // Les plus récemment actifs d'abord : un parcours qui vient de bouger
        // est celui qu'on surveille.
        .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    [users]
  );

  const stepOptions = useMemo(
    () =>
      Object.fromEntries(
        Array.from(new Set(rows.map((r) => r.etape))).map((l) => [l, l])
      ) as Record<string, string>,
    [rows]
  );

  const columns = [
    {
      dataField: "utilisateur",
      text: "Utilisateur",
      filter: textFilter({ placeholder: "Chercher..." }),
      formatter: (cell: string, row: Row) => (
        <div>
          <div className="fw-semibold">{cell}</div>
          <small className="text-muted">{row.email}</small>
        </div>
      ),
    },
    {
      dataField: "entreprise",
      text: "Entreprise",
      filter: textFilter({ placeholder: "Chercher..." }),
      formatter: (cell: string, row: Row) => (
        <div>
          <div>{cell}</div>
          <small className="text-muted">{row.identifiant}</small>
        </div>
      ),
    },
    {
      dataField: "etape",
      text: "Étape",
      filter: selectFilter({ options: stepOptions, placeholder: "Choisir" }),
      formatter: (cell: string, row: Row) => {
        const step = getOnboardingStep(row.statut);
        return (
          <p
            className="mb-0"
            title={step.hint}
            style={{
              display: "inline-block",
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: 12,
              lineHeight: "18px",
              color: "#ffffff",
              background: step.color,
              borderRadius: 5,
              padding: "6px 10px",
              whiteSpace: "nowrap",
            }}
          >
            {step.label}
          </p>
        );
      },
    },
    {
      dataField: "uid",
      text: "Détails",
      formatter: (cell: string, row: Row) => {
        const b = row.user.bcs || {};
        const link = getMagicLinkState(b.onboarding);
        const err = b.error;
        return (
          <div style={{ fontSize: 13, lineHeight: "20px" }}>
            {b.tenantSlug && (
              <div>
                <span className="text-muted">Tenant : </span>
                <code>{b.tenantSlug}</code>
              </div>
            )}
            {b.onboarding?.providerStatus && (
              <div>
                <span className="text-muted">Parcours : </span>
                {b.onboarding.providerStatus}
              </div>
            )}
            {link && (
              <div style={{ color: link.expired ? "#c0392b" : undefined }}>
                {link.label}
              </div>
            )}
            {!!b.onboarding?.restartAttempts && (
              <div>
                <span className="text-muted">Reprises : </span>
                {b.onboarding.restartAttempts}
              </div>
            )}
            {err && (
              <div style={{ color: "#c0392b" }}>
                {[err.code, err.message].filter(Boolean).join(" — ")}
                {err.detail && (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {err.detail}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataField: "updatedAt",
      text: "Mis à jour",
      sort: true,
      formatter: (cell?: number) => dateTime(cell),
      headerStyle: () => ({ width: "140px" }),
    },
  ];

  return (
    <BootstrapTable
      keyField="uid"
      data={rows}
      columns={columns}
      bordered={true}
      condensed={false}
      responsive={true}
      wrapperClasses="table-responsive admin-table"
      noDataIndication={() => "Aucun onboarding en cours"}
      filter={filterFactory()}
    />
  );
}

export default OnboardingsTable;
